// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { Button, Modal as FinModal, SearchBar } from '@loa/ui-components';
import { useNavigate } from 'react-router-dom';
import { clientListApi } from '../../api/clientListApi';
import { policyApi } from '../../api/policyApi';
import type { ClientPolicyDTO } from '../../types/clientList';
import type { Policy, PolicyCreateDTO, PolicyUpdateDTO } from '../../types/policy';
import { PolicyList } from '../PolicyList';
import { PolicyForm } from '../PolicyForm';
import './ClientPoliciesTab.css';

type ClientPoliciesTabProps = Readonly<{
  clientId: string;
  profileId: string;
}>;

function clientPolicyToPolicy(dto: ClientPolicyDTO): Policy {
  return {
    id: dto.policyId,
    profileId: '',
    brokerageId: '',
    policyNumber: dto.policyNumber,
    category: dto.category,
    policyType: dto.policyTypeName || dto.category || '',
    policyTypeName: dto.policyTypeName,
    providerName: dto.providerName,
    providerLogoUrl: dto.providerLogoUrl,
    carrier: dto.providerName || '',
    premiumAmount: dto.premiumAmount ?? 0,
    status: (dto.policyStatus?.toLowerCase() || 'active') as Policy['status'],
    ownershipType: (dto.ownershipType || 'SOLO') as Policy['ownershipType'],
    ownerCount: dto.ownerCount,
    loaCount: dto.loaCount,
    completedLoaCount: dto.completedLoaCount,
    providerRejected: dto.providerRejected,
    documentStatus: dto.documentStatus as Policy['documentStatus'],
    updatedAt: dto.updatedAt,
    signatories: (dto.signatories || []).map((s, i) => ({
      id: `${dto.policyId}-sig-${i}`,
      policyId: dto.policyId,
      clientId: '',
      clientName: '',
      clientEmail: '',
      relationshipType: s.relationshipType as 'OWNER' | 'SIGNATORY' | 'OWNER_AND_SIGNATORY',
      signingOrder: i + 1,
      isSigned: false,
      createdAt: '',
      signatoryStatus: s.signatoryStatus,
    })),
    createdBy: '',
    createdAt: '',
  };
}

export function ClientPoliciesTab({ clientId, profileId }: ClientPoliciesTabProps) {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState<ClientPolicyDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Remove relationship modal
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);

  // Add policy modal
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [submittingPolicy, setSubmittingPolicy] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);

  const loadPolicies = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await clientListApi.getClientPolicies(clientId);
      setPolicies(data);
    } catch (err) {
      console.error('Failed to load policies:', err);
      setError((err as Error).message || 'Failed to load policies');
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const handleRemoveRelationship = async () => {
    if (!selectedPolicy) return;

    try {
      setRemovingId(selectedPolicy.id);
      await clientListApi.removePolicyRelationship(clientId, selectedPolicy.id);
      await loadPolicies();
      setShowRemoveConfirm(false);
      setSelectedPolicy(null);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Error removing relationship:', error);
      alert(error.response?.data?.message || 'Failed to remove relationship');
    } finally {
      setRemovingId(null);
    }
  };

  const handleAddPolicy = () => {
    if (!profileId) {
      alert('Unable to add policy: no linked relationship found for this client.');
      return;
    }
    setPolicyError(null);
    setShowPolicyModal(true);
  };

  const handleClosePolicyModal = () => {
    setShowPolicyModal(false);
    setPolicyError(null);
  };

  const handleSubmitPolicy = async (data: PolicyCreateDTO | PolicyUpdateDTO) => {
    try {
      setSubmittingPolicy(true);
      setPolicyError(null);
      const createData = data as PolicyCreateDTO;
      // For SOLO policies, auto-assign the current client as owner and signatory
      if ((!createData.ownershipType || createData.ownershipType === 'SOLO') && clientId) {
        createData.signatoryClientIds = [clientId];
      }
      await policyApi.createPolicy(profileId, createData);
      await loadPolicies();
      handleClosePolicyModal();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Error creating policy:', error);
      setPolicyError(error.response?.data?.message || 'Failed to create policy');
    } finally {
      setSubmittingPolicy(false);
    }
  };

  // Map DTOs to Policy type for PolicyList
  const mappedPolicies = policies.map(clientPolicyToPolicy);

  // Client-side search filtering
  const filteredPolicies = searchQuery
    ? mappedPolicies.filter(p => {
        const q = searchQuery.toLowerCase();
        return (p.providerName || p.carrier || '').toLowerCase().includes(q)
          || (p.policyTypeName || p.category || '').toLowerCase().includes(q);
      })
    : mappedPolicies;

  // Build set of removable policy IDs
  const removablePolicyIds = new Set(
    policies
      .filter(p => p.canRemoveOwnership || p.canRemoveSignatory)
      .map(p => p.policyId)
  );

  return (
    <div className="policies-tab">
      <div className="policies-tab-header">
        <h3 className="policies-tab-title">Policies</h3>
        <div className="policies-tab-search-row">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by provider or policy type"
          />
          <Button variant="filled" size="lg" onClick={handleAddPolicy}>
            Add Policy
          </Button>
        </div>
      </div>

      <PolicyList
        policies={filteredPolicies}
        loading={isLoading}
        error={error}
        onEdit={() => {}}
        onDelete={() => {}}
        onManageRelationships={() => {}}
        onRowClick={(policy) => navigate(`/policies/${policy.id}`)}
        onRemoveRelationship={(policy) => {
          setSelectedPolicy(policy);
          setShowRemoveConfirm(true);
        }}
        removablePolicyIds={removablePolicyIds}
      />

      {/* Remove Confirmation Modal */}
      <FinModal open={showRemoveConfirm} onClose={() => { setShowRemoveConfirm(false); setSelectedPolicy(null); }} size="small" variant="warning">
        <FinModal.Header title="Remove Policy Relationship" onClose={() => { setShowRemoveConfirm(false); setSelectedPolicy(null); }} />
        <FinModal.Content>
          <p>
            Are you sure you want to remove this client's relationship with policy{' '}
            <strong>{selectedPolicy?.policyNumber}</strong>?
          </p>
          <p>This will affect any LOAs that include this policy.</p>
        </FinModal.Content>
        <FinModal.Actions
          secondaryAction={{ label: 'Cancel', onClick: () => { setShowRemoveConfirm(false); setSelectedPolicy(null); } }}
          primaryAction={{ label: 'Remove Relationship', onClick: () => { void handleRemoveRelationship(); }, colour: 'red', loading: !!removingId }}
        />
      </FinModal>

      {/* Add Policy Modal */}
      <FinModal open={showPolicyModal} onClose={handleClosePolicyModal} size="medium">
        <FinModal.Header title="Add New Policy" onClose={handleClosePolicyModal} />
        <FinModal.Content>
          <PolicyForm
            onSubmit={handleSubmitPolicy}
            onCancel={handleClosePolicyModal}
            loading={submittingPolicy}
            error={policyError}
          />
        </FinModal.Content>
      </FinModal>
    </div>
  );
}
